/**
 * What: The closed set of typed blocks the Worker sends for a Study Builder section, and the sources the app echoes back.
 * Transport / data-flow: Built on the Worker (`apps/shopify/src/study-brief/audience-sections.ts`), validated here, drawn by one
 *   generic renderer in the app. The renderer owns layout, focus and accessibility only; it holds no option lists, headings or
 *   fallbacks. A field's `key` names the draft field the app reports back in an edit; the server maps it.
 *
 * Adding a block kind is a schema change on both sides. There is deliberately no `custom` kind.
 */
import * as z from 'zod';
import { builderSuggestionFeedbackBlockSchema } from './shopify-study-builder-suggestion-feedback-block.js';
const option = z.strictObject({ value: z.string(), label: z.string(), disabled: z.boolean().optional() });
/**
 * The hover text of the "*" the page draws on a required thing. ONE definition: a field carries it, and so does a section
 * header (the whole section is a required choice). Absent: not required.
 */
const requiredReasonSchema = z.string().min(1).optional();
/**
 * The one message model for a field. `message` is what is wrong with the field (red `error`, amber `warning`); the Worker sends
 * it for a field that HAS content and fails its rule, in every answer; a field with no content never gets one (it has only its
 * required marker). A warning never holds Submit shut.
 * `waitingOn` says the field is switched off because it waits on something else (`key` names it); `text` is the Worker's
 * words, drawn under the field. The Worker sets `disabled` together with it.
 */
export const builderFieldMessageSchema = z.strictObject({
    severity: z.enum(['error', 'warning']),
    text: z.string().min(1),
});
export const builderWaitingOnSchema = z.strictObject({ key: z.string().min(1), text: z.string().min(1) });
const messages = {
    message: builderFieldMessageSchema.nullable(),
    waitingOn: builderWaitingOnSchema.nullable(),
};
/**
 * One field change the app reports, for any field of any section. The server decides what it means (market change clears
 * income, preset sets ages, a title sets the title). The app keeps every unacknowledged edit and sends them all, in
 * order, so an aborted request never loses one. The app never composes a `value`: the Worker puts the finished `{key, value}`
 * on each choice it sends (a tile's `id`, a field-picker choice's `edit`), and the app reports exactly that.
 */
export const builderEditSchema = z.strictObject({ key: z.string().min(1), value: z.string() });
/**
 * The one word a section's working cue shows while an evaluation the merchant started FROM THIS block is in flight. The
 * Worker puts it on each field/action block (and the field picker) so the app holds it the moment the block is clicked or
 * typed; the app looks it up by the in-flight edit key and shows it in that block's section only — it never picks or
 * composes wording. Absent: this block starts no visible cue (a plain text field that needs none).
 */
const workingPhraseSchema = z.string().min(1).optional();
/**
 * Polaris requires a label on every field, so it is always present. `requiredReason` is present only on a field the person
 * must fill in before Submit opens: it is the hover text of the "*" the page draws on the label.
 */
const field = {
    key: z.string().min(1),
    label: z.string().min(1),
    ...messages,
    disabled: z.boolean(),
    requiredReason: requiredReasonSchema,
    working: workingPhraseSchema,
};
export const builderSelectBlockSchema = z.strictObject({
    kind: z.literal('select'),
    ...field,
    details: z.string().nullable(),
    value: z.string(),
    options: z.array(option),
});
export const builderNumberBlockSchema = z.strictObject({
    kind: z.literal('number'),
    ...field,
    value: z.string(),
    placeholder: z.string().nullable(),
});
/** Typed text. `rows` null is one line; a number is a multi-line box that tall. Reported on commit, like `number`. */
export const builderTextBlockSchema = z.strictObject({
    kind: z.literal('text'),
    ...field,
    value: z.string(),
    placeholder: z.string().nullable(),
    rows: z.number().int().positive().nullable(),
    /** Shown before the typed text on one-line fields (a currency symbol). Absent: none. */
    prefix: z.string().optional(),
    /** The most characters the field accepts, drawn as a character counter. Absent: no limit. (Done like `prefix`.) */
    maxLength: z.number().int().positive().optional(),
});
export const builderRowBlockSchema = z.strictObject({
    kind: z.literal('row'),
    blocks: z
        .array(z.discriminatedUnion('kind', [builderSelectBlockSchema, builderNumberBlockSchema]))
        .min(1)
        .max(4),
});
/** The few things only the browser can do; the Worker names one and the page supplies the handler. */
export const clientActionSchema = z.enum(['choose-product']);
/**
 * The closed set of heading sizes the summary sidebar uses, drawn by the one `s-heading` owner (`BlockHeading`). Absent: the
 * default heading size (a section title). `large-200` is the sidebar's top heading; `large` its sub-section headings.
 */
export const builderHeadingSizeSchema = z.enum(['large', 'large-200']);
/**
 * The closed set of browser-rendered summary pieces the Worker PLACES by kind (like a `client` action): a composite the app
 * draws from the Worker-sent summary plus the finished account/history sidebar operation. The Worker owns WHERE each
 * sits and what it means; the browser owns how it looks.
 */
export const builderClientComponentSchema = z.enum([
    'summary-product',
    'summary-testing',
    'summary-audience',
    'summary-shoppers',
]);
/**
 * The closed set of Polaris icons a button may draw. Parsed, not a free-form string, so the
 * renderer passes `block.icon` straight to `s-button icon=` with no cast. Every value is a valid Polaris `IconType`.
 */
export const builderActionIconSchema = z.enum(['thumbs-up', 'thumbs-down']);
export const builderTilesBlockSchema = z
    .strictObject({
    kind: z.literal('tiles'),
    key: z.string().min(1),
    value: z.string().nullable(),
    ...messages,
    disabled: z.boolean(),
    working: workingPhraseSchema,
    /**
     * Standard "Show more" behaviour: the Worker marks the tiles past `visible` as collapsed. The shared renderer draws the
     * first `visible` tiles plus a `more` button; pressing it reveals the rest and swaps the button to `fewer`. Expanding is
     * local UI state in the renderer, never a round trip. Absent: every tile is shown (no button). The Worker is responsible
     * for ordering the tiles so the ones that must always be visible (the chosen tile) sit within `visible`.
     */
    collapse: z
        .strictObject({
        visible: z.number().int().nonnegative(),
        more: z.string().min(1),
        fewer: z.string().min(1),
    })
        .optional(),
    tiles: z.array(z.strictObject({
        id: z.string().min(1),
        title: z.string().min(1),
        detail: z.string(),
        body: z.string().nullable(),
        icon: z.string().nullable(),
        /** The only dimming signal, independent of selection: a tile that is shown but cannot be chosen. */
        disabled: z.boolean(),
        /** A preview picture for the tile (the picker's product thumbnail). Null: no picture. (Picker work is part C.) */
        image: z.strictObject({ src: z.string().min(1), alt: z.string() }).nullable(),
        /** A fixed browser handler this tile runs instead of reporting a choice (open the comparison picker). */
        client: clientActionSchema.nullable(),
        /** A small label at the foot of the tile, for example "Suggested". Absent: none. Not a selection signal. */
        badge: z
            .strictObject({ text: z.string().min(1), tone: z.enum(['info', 'success', 'warning']).nullable() })
            .optional(),
    })),
})
    // Selection is coherent: a non-null value names exactly one tile, so the chosen tile is always present and ticked.
    .superRefine((block, ctx) => {
    if (block.value !== null && block.tiles.filter((tile) => tile.id === block.value).length !== 1)
        ctx.addIssue({
            code: 'custom',
            path: ['value'],
            message: `tiles value "${block.value}" must match exactly one tile id`,
        });
});
export const builderNoteBlockSchema = z.strictObject({
    kind: z.literal('note'),
    text: z.string().min(1),
    /** `base` is plain default-colour text; `subdued` grey; `critical`/`warning` the red/amber tones. */
    tone: z.enum(['base', 'subdued', 'critical', 'warning']),
    /** How the note reads: `heading` draws a heading, `strong` draws bold text. Absent: a plain line in its `tone`. */
    emphasis: z.enum(['heading', 'strong']).optional(),
    /** Draws a spinner before the text (a working line, e.g. "Preparing test variations…"). Absent: a plain line. */
    busy: z.boolean().optional(),
});
/** A horizontal rule between groups (the summary sidebar's section dividers). No fields; the renderer draws `s-divider`. */
export const builderDividerBlockSchema = z.strictObject({ kind: z.literal('divider') });
/**
 * A browser-rendered composite the Worker places by kind (`component`): the renderer looks it up in the page's client-component
 * registry and draws it from data the app already holds. Read-only; it reports no edit. See `builderClientComponentSchema`.
 */
export const builderClientBlockSchema = z.strictObject({
    kind: z.literal('client'),
    component: builderClientComponentSchema,
});
/**
 * A tick box the merchant toggles. It reports its `key` as `'on'` (ticked) or `'off'` (unticked) through the ordinary edit
 * mechanism, so the server decides what each tick means. `details` is the subdued line under the label; `badge` is a pill
 * drawn above the box (the suggestion's category, info-toned when ticked). The own-questions suggestions use these.
 */
export const builderCheckboxBlockSchema = z.strictObject({
    kind: z.literal('checkbox'),
    key: z.string().min(1),
    label: z.string().min(1),
    details: z.string().nullable(),
    checked: z.boolean(),
    badge: z
        .strictObject({ text: z.string().min(1), tone: z.enum(['info', 'success', 'warning', 'critical']).nullable() })
        .nullable(),
    ...messages,
    disabled: z.boolean(),
    working: workingPhraseSchema,
});
/**
 * A field picker: the "What would you like to test?" section's two sides. LEFT a narrow, scrollable list of supported
 * field names from the chosen version; RIGHT the picked
 * field's full value and a single "Test this field as" choice whose pick creates or updates the test's comparison, plus
 * the standard shopper-question cards for the chosen concept. The Worker builds every row and its detail; the page draws
 * them and holds only which row is viewed. Each concept choice carries its finished `comparisons.add` `edit`; picking one
 * reports that edit verbatim.
 */
/** What a field value looks like, already decided by the Worker: the page never inspects a value to guess its kind. */
export const fieldPickerPreviewSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('text'), text: z.string() }),
    z.strictObject({ kind: z.literal('image'), url: z.string().min(1), alt: z.string() }),
]);
/**
 * One "Test this field as" row: server-owned availability and wording; `suggestedBadge` marks suggested concepts.
 * `edit` is the finished `{key, value}` the app reports when this choice is picked — the Worker builds it from the field's
 * identity and this concept (`packComparisonAddEdit`), so the app composes nothing and re-validates no id.
 */
export const fieldPickerChoiceSchema = z.strictObject({
    conceptId: z.string().min(1),
    label: z.string().min(1),
    description: z.string(),
    suggestedBadge: z.string().min(1).nullable(),
    disabled: z.boolean(),
    unavailableLabel: z.string().min(1).nullable(),
    edit: builderEditSchema,
});
/** How `comparisons.add` packs its value — the one place the grammar `<concept>|<metafieldId>|<variantId>` is written. */
export function packComparisonAddEdit(conceptId, metafieldId, variantId) {
    return { key: 'comparisons.add', value: `${conceptId}|${metafieldId}|${variantId ?? ''}` };
}
export const fieldPickerRowSchema = z.strictObject({
    /** Stable id for the viewed-row state and React keys: `<metafieldId>|<variantId>`. */
    id: z.string().min(1),
    label: z.string().min(1),
    /** The full value for the detail pane. */
    preview: fieldPickerPreviewSchema,
    /** Said under the detail value; null when there is nothing to add. */
    valuesNote: z.string().nullable(),
    /** The single-choice rows the field can be tested as (every supported concept, suggested first); empty when none fit. */
    choices: z.array(fieldPickerChoiceSchema),
    /** The concept this test uses for this field (its comparisonType), or null when none is picked. */
    selectedConceptId: z.string().nullable(),
});
export const builderFieldPickerBlockSchema = z
    .strictObject({
    kind: z.literal('field-picker'),
    /** The left list's heading. */
    fieldsHeading: z.string().min(1),
    /** The detail value section's heading. */
    valuesHeading: z.string().min(1),
    /** The "Test this field as" heading, shown above the concept choice for every field. */
    testAsHeading: z.string().min(1),
    /** Shown in place of the choice for a field no concept fits. */
    noConceptsLabel: z.string().min(1),
    /** Switches the whole picker off (greyed, not editable), the same way every field block carries `disabled`. The app
     * draws it disabled when this OR the global structure lock is set. A waiting "What would you like to test?" section sends
     * it true over its stand-in rows; the active section sends it false. */
    disabled: z.boolean(),
    /** The id of the row whose detail shows on the right by default (the comparison's field, else the first row). */
    selectedId: z.string().min(1),
    /** Count and explanation for fields unavailable to test; null when no fields are hidden. */
    hiddenFields: z
        .strictObject({
        label: z.string().min(1),
        accessibilityLabel: z.string().min(1),
        explanation: z.string().min(1),
    })
        .nullable(),
    /** The working-cue word for the edit this block starts (picking a concept reports `comparisons.add`). */
    working: workingPhraseSchema,
    rows: z.array(fieldPickerRowSchema).min(1),
})
    // Selection is coherent: `selectedId` names exactly one row in the complete scrollable list.
    .superRefine((block, ctx) => {
    const index = block.rows.findIndex((row) => row.id === block.selectedId);
    if (index < 0 || block.rows.filter((row) => row.id === block.selectedId).length !== 1)
        ctx.addIssue({
            code: 'custom',
            path: ['selectedId'],
            message: `field-picker selectedId "${block.selectedId}" must match exactly one row id`,
        });
});
/**
 * A button. Pressing it reports `{key, value: 'press'}` and the server decides what it does, unless `client` names one of
 * the few things only the browser can do (open Shopify's own picker or panel): the renderer then runs that fixed handler.
 */
export const builderActionBlockSchema = z.strictObject({
    kind: z.literal('action'),
    key: z.string().min(1),
    /** Opaque Worker-owned edit value; omitted for ordinary press actions. The renderer forwards it unchanged. */
    value: z.string().optional(),
    label: z.string().min(1),
    disabled: z.boolean(),
    /** How the button reads. The Worker always sets it: `primary` is the lead button, `tertiary` a plain link-like button. */
    variant: z.enum(['primary', 'secondary', 'tertiary']),
    /** `critical` draws the destructive (red) treatment, e.g. Remove. Null: the neutral treatment. */
    tone: z.enum(['critical']).nullable(),
    /** The button's accessible name, when several buttons share one visible label ("Remove"). Absent: the label is used. */
    accessibilityLabel: z.string().min(1).optional(),
    /**
     * A Polaris icon drawn on the button. With an `accessibilityLabel` and no visible `label` it is an icon-only button (the
     * suggestion vote controls). Absent: a text button. A closed set (the vote controls) parsed here, so the page passes it
     * straight to `s-button icon=` with no cast; adding an icon is a contract change, never a free-form string.
     */
    icon: builderActionIconSchema.optional(),
    /** `constrained` caps the button at `min(100%, 280px)` (full width on phones), the suggestion "Use this idea" button's
     * sizing. Absent: the button sizes to its content. A layout hint only, so any section can reuse it. */
    width: z.enum(['constrained']).optional(),
    working: workingPhraseSchema,
    client: clientActionSchema.nullable(),
});
/**
 * A warning/status banner (Polaris `s-banner`): an optional `heading`, one line of `text`, and an optional `action` button.
 * The one block drawn as a true banner — the look the old builder used for its warnings — as opposed to the plain tinted
 * `note` line. `tone` is the s-banner tone. The three places it is used: comparison field findings (warning, text only),
 * audience too small (warning, text + the Reset action), currency unresolved (critical, heading "Currency unresolved").
 * The `action` is an ordinary action block the renderer draws inside the banner, so a banner needs no special button rules.
 */
export const builderBannerBlockSchema = z.strictObject({
    kind: z.literal('banner'),
    tone: z.enum(['info', 'success', 'warning', 'critical']),
    heading: z.string().min(1).nullable(),
    text: z.string().min(1),
    action: builderActionBlockSchema.nullable(),
});
/** A read-only product card: the image, the title, a badge and one detail line, all built by the Worker. */
export const builderProductBlockSchema = z.strictObject({
    kind: z.literal('product'),
    title: z.string().min(1),
    image: z.strictObject({ src: z.string().min(1), alt: z.string() }).nullable(),
    badge: z
        .strictObject({ text: z.string().min(1), tone: z.enum(['info', 'success', 'warning', 'critical']) })
        .nullable(),
    detail: z.string(),
});
/**
 * A picture the merchant uploads, drawn as the old comparison image tile: a 100px square media cell (the house art and
 * "Add image" when empty, the picture when set), the file name under the label, and the Replace and Remove buttons in one
 * actions cell. `value` is the stored picture's address ('' for none). The app sends the chosen file and reports the new
 * address as the edit; the Worker owns the type and size rules and refuses a wrong file with its own `merchantMessage`, so
 * the app runs no check of its own. `accept` is still drawn as the picker's `<input accept>` hint. Every other word it
 * shows is in `labels`.
 *
 * `filename` is the readable name the Worker derives from `value` ('' when none) — the server owns the words, so the app
 * never parses the address itself (it shows the in-flight file's own name only while an upload is running).
 * `remove` is the tile's own Remove action, sent by the Worker: present on an editable option (its `key` is the same
 * `…options.<id>.remove` edit the text option Remove reports), null on the read-only original-product tile. A tile is
 * editable (clickable media, drag-and-drop, Replace + Remove) exactly when `remove` is present.
 */
export const builderUploadBlockSchema = z.strictObject({
    kind: z.literal('upload'),
    ...field,
    value: z.string(),
    /** The readable file name the Worker derives from `value`; '' when there is no picture. */
    filename: z.string(),
    /** The tile's own Remove action (Worker-sent, same edit the text option Remove uses). Null: a read-only tile (original). */
    remove: z
        .strictObject({ key: z.string().min(1), label: z.string().min(1), accessibilityLabel: z.string().min(1) })
        .nullable(),
    /** The file types the picker offers, as an `<input accept>` value. */
    accept: z.string().min(1),
    maxBytes: z.number().int().positive(),
    labels: z.strictObject({
        choose: z.string().min(1),
        replace: z.string().min(1),
        empty: z.string().min(1),
        uploading: z.string().min(1),
        alt: z.string().min(1),
        notImage: z.string().min(1),
        tooLarge: z.string().min(1),
        failed: z.string().min(1),
    }),
});
/**
 * The closed set of art the page draws. A key into the page's small art registry; the renderer parses it, so an art key the
 * registry does not know fails the contract rather than silently drawing nothing. Null: no art.
 */
export const builderIllustrationSchema = z.enum(['comparison']);
/**
 * How a group draws itself, named by the Worker — the page derives NOTHING from the title or badge. Each value is one box
 * treatment, all drawn by the one `GroupBlock`:
 * - `rows`: a bordered box, children stacked (option rows, the original-product row, a subdued panel).
 * - `inline-between`: a bordered box, children on one row pushed apart (the own-questions count + Add row).
 * - `field-action`: borderless field + trailing action, stacked below 480px (inside an option's bordered row).
 * - `card`: a titled `BuilderBox` card (a price suggestion, an AI idea, a standard shopper-question card).
 * - `illustration-card`: a subdued example card with the `glyph` emoji centred above a heading and the nested blocks (the
 *   image-comparison examples 🍾 / 🍾 🥂).
 * - `grid`: a two-up grid (the four price suggestion cards).
 * - `card-grid`: a grid of `card`/`illustration-card` children at the shared BuilderBox tile width (the standard
 *   shopper-question cards, the image-comparison examples).
 * - `intro`: an illustrated introduction — the registry art beside a heading and the nested blocks.
 * - `inline` / `between` / `end`: BORDERLESS rows (no box, no padding) — the per-comparison header, the add/votes row, the
 *   "Show another set" row — start-aligned, space-between and end-aligned respectively.
 */
export const builderGroupStyleSchema = z.enum([
    'rows',
    'inline-between',
    'field-action',
    'card',
    'illustration-card',
    'grid',
    'card-grid',
    'intro',
    'inline',
    'between',
    'end',
]);
/**
 * Recursion with a discriminatedUnion (zod 4): the group stays a plain `strictObject` (so it keeps a literal `kind` and a
 * `.shape`, which `discriminatedUnion` needs and a test reads), and only its `blocks` member is deferred with a getter that
 * returns `z.array(builderBlockSchema)`. The getter's return type is pinned to `z.ZodType<BuilderBlock[]>` so TypeScript uses
 * the hand-written `BuilderBlock` type and never tries to infer the cyclic schema (no "excessively deep" error, no `any`).
 */
export const builderGroupBlockSchema = z.strictObject({
    kind: z.literal('group'),
    badge: z
        .strictObject({ text: z.string().min(1), tone: z.enum(['info', 'success', 'warning', 'critical']).nullable() })
        .nullable(),
    title: z.string().nullable(),
    background: z.enum(['base', 'subdued']),
    illustration: builderIllustrationSchema.nullable(),
    style: builderGroupStyleSchema,
    /** The hover text of a required-choice "*" beside a `rows` badge; sent on the group, not read from a child. Absent: none. */
    requiredReason: requiredReasonSchema,
    /** The group's own fields drop their labels to assistive-tech-only (the badge names the whole row). */
    labelChildren: z.boolean(),
    /** The large emoji an `illustration-card` centres above its title. Absent on every other style. */
    glyph: z.string().min(1).optional(),
    get blocks() {
        return z.array(builderBlockSchema);
    },
});
export const builderBlockSchema = z.discriminatedUnion('kind', [
    builderSelectBlockSchema,
    builderNumberBlockSchema,
    builderTextBlockSchema,
    builderRowBlockSchema,
    builderTilesBlockSchema,
    builderNoteBlockSchema,
    builderBannerBlockSchema,
    builderDividerBlockSchema,
    builderClientBlockSchema,
    builderCheckboxBlockSchema,
    builderFieldPickerBlockSchema,
    builderActionBlockSchema,
    builderSuggestionFeedbackBlockSchema,
    builderProductBlockSchema,
    builderUploadBlockSchema,
    builderGroupBlockSchema,
]);
/** Every kind the renderer must draw; a test and the renderer's exhaustive switch both check against it. */
export const builderBlockKinds = [
    'select',
    'number',
    'text',
    'row',
    'tiles',
    'note',
    'banner',
    'divider',
    'client',
    'checkbox',
    'field-picker',
    'action',
    'suggestion-feedback',
    'product',
    'upload',
    'group',
];
/**
 * What each kind is, said once. A `field` is something the person edits or presses: it carries `message` and `waitingOn`. A
 * `container` holds other blocks. `info` is read-only or a button. Every kind must be listed (the `satisfies` fails the
 * type-check for a new kind), and the router and the renderer read every `field` block's `message`, so a field kind without the
 * message pair fails type-check: a new field kind cannot be added without message support.
 */
export const builderBlockClass = {
    select: 'field',
    number: 'field',
    text: 'field',
    tiles: 'field',
    checkbox: 'field',
    upload: 'field',
    row: 'container',
    group: 'container',
    note: 'info',
    banner: 'info',
    divider: 'info',
    client: 'info',
    action: 'info',
    'suggestion-feedback': 'info',
    product: 'info',
    // Interactive (a concept pick reports `comparisons.add`) but not a single message-bearing field, like `action`.
    'field-picker': 'info',
};
export const isFieldBlock = (block) => builderBlockClass[block.kind] === 'field';
/** The tones a section badge can take. The Worker chooses; the app draws. */
export const builderBadgeToneSchema = z.enum(['info', 'success', 'warning', 'critical']);
/** A section's header: what the frame draws above the body. */
export const builderSectionHeaderSchema = z.strictObject({
    title: z.string().min(1),
    description: z.string(),
    badge: z.strictObject({ text: z.string().min(1), tone: builderBadgeToneSchema }).nullable(),
    /** One button for the whole section, drawn on the right of the header. */
    action: builderActionBlockSchema.nullable(),
    /** Present when the whole section is a required choice (the comparison): the hover text of the "*" drawn beside the title. */
    requiredReason: requiredReasonSchema,
    /** The title's heading size; the summary sidebar's top heading uses `large-200`. Absent: the default section-title size. */
    size: builderHeadingSizeSchema.optional(),
});
const populationSchema = z.object({
    id: z.string().min(1),
    display_name: z.string().min(1),
    n_total: z.number().int().nonnegative(),
    source: z.string().min(1),
    metadata: z.record(z.string(), z.unknown()).optional(),
    availability: z.record(z.string(), z.unknown()).optional(),
    filter_options: z.object({ income_bands: z.array(z.string()), employment_statuses: z.array(z.string()) }).optional(),
});
const frameLeverSchema = z.looseObject({
    id: z.string().min(1),
    label: z.string(),
    targetDistribution: z.record(z.string(), z.number()).optional(),
});
const frameGroupSchema = z.object({
    label: z.string().min(1),
    count: z.number().nullable(),
    share: z.number().nullable(),
    suppressed: z.boolean(),
});
/**
 * Sources the server read for the audience sections. The app echoes them back untouched on the next request, so an edit does
 * not read the population registry or Tinybird again. (Product research sources are not echoed — they are projected from the
 * stored product pick each evaluate — so the audience echo is now the only echoed-source mechanism.)
 */
export const audienceSourcesSchema = z.strictObject({
    populations: z.array(populationSchema),
    customerGroupFrame: z
        .strictObject({
        groups: z.array(frameGroupSchema),
        frameLevers: z.array(frameLeverSchema),
        asOf: z.string().nullable(),
        incomplete: z.boolean(),
    })
        .nullable(),
});
//# sourceMappingURL=shopify-study-builder-blocks.js.map